// ═══════════════════════════════════════════════════════════════════════════════
// Common Validation Schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { DATE_REGEX, DATETIME_REGEX, PASSWORD_REGEX, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, NICKNAME_MIN_LENGTH, NICKNAME_MAX_LENGTH, USAGE_REGION_VALUES, } from '../constants/index.js';
// ── Date Schema (YYYY-MM-DD) ──
export const dateSchema = z.string().regex(DATE_REGEX, 'Date must be in YYYY-MM-DD format');
// ── Optional Date Schema (YYYY-MM-DD or "") ──
export const optionalDateSchema = z.string().refine((val) => val === '' || DATE_REGEX.test(val), 'Date must be in YYYY-MM-DD format or empty');
// ── Datetime Schema (YYYY-MM-DD HH:mm) ──
export const datetimeSchema = z.string().regex(DATETIME_REGEX, 'Datetime must be in YYYY-MM-DD HH:mm format');
// ── Optional Datetime Schema (YYYY-MM-DD HH:mm or "") ──
export const optionalDatetimeSchema = z.string().refine((val) => val === '' || DATETIME_REGEX.test(val), 'Datetime must be in YYYY-MM-DD HH:mm format or empty');
// ── Time Schema (HH:mm) ──
export const timeSchema = z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Time must be in HH:mm format');
// ── Optional Time Schema (HH:mm or "") ──
export const optionalTimeSchema = z.string().refine((val) => val === '' || /^([01]\d|2[0-3]):([0-5]\d)$/.test(val), 'Time must be in HH:mm format or empty');
// ── Email Schema ──
export const emailSchema = z.string()
    .email('Invalid email format')
    .max(255, 'Email must not exceed 255 characters')
    .transform((val) => val.toLowerCase().trim());
// ── Password Schema ──
export const passwordSchema = z.string()
    .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
    .max(PASSWORD_MAX_LENGTH, `Password must not exceed ${PASSWORD_MAX_LENGTH} characters`)
    .regex(PASSWORD_REGEX, 'Password must contain at least 1 uppercase letter, 1 lowercase letter, and 1 digit');
// ── Nickname Schema ──
export const nicknameSchema = z.string()
    .min(NICKNAME_MIN_LENGTH, `Nickname must be at least ${NICKNAME_MIN_LENGTH} characters`)
    .max(NICKNAME_MAX_LENGTH, `Nickname must not exceed ${NICKNAME_MAX_LENGTH} characters`)
    .transform((val) => val.trim());
// ── UUID Schema ──
export const uuidSchema = z.string().uuid('Invalid UUID format');
// ── Positive Integer Schema ──
export const positiveIntSchema = z.number()
    .int('Must be an integer')
    .positive('Must be a positive number');
// ── Non-negative Number Schema ──
export const nonNegativeNumberSchema = z.number()
    .min(0, 'Must be zero or positive');
// ── Pagination Schema ──
export const paginationSchema = z.object({
    page: z.number().int().min(1).default(1),
    pageSize: z.number().int().min(1).max(100).default(20),
});
// ── Empty String or Number Schema (for AI fields) ──
export const emptyOrNumberSchema = z.union([
    z.number(),
    z.literal(''),
]);
// ── Empty String or String Schema ──
export const emptyOrStringSchema = z.union([
    z.string(),
    z.literal(''),
]);
// ── Currency Code Schema ──
export const currencyCodeSchema = z.string()
    .regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter uppercase code')
    .or(z.literal(''));
// ── Timezone Schema ──
export const timezoneSchema = z.string()
    .min(1, 'Timezone is required')
    .refine((tz) => {
    try {
        Intl.DateTimeFormat(undefined, { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, 'Invalid timezone');
// ── Usage Region Schema ──
export const usageRegionSchema = z.enum(USAGE_REGION_VALUES, { error: () => ({ message: 'Invalid usage region' }) });
//# sourceMappingURL=common.schema.js.map